import React, { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Shared option-list behaviour for the searchable dropdowns in EmailLog.tsx,
 * LogTimeslipForm.tsx and LogClarraDocsForm.tsx.
 *
 * All three files previously carried their own copy of the same filter/sort
 * and rendered EVERY match into the DOM. With ~50k matters / ~100k contacts
 * that meant tens of thousands of <div>s created the moment the list opened -
 * the `max-h-60 overflow-auto` wrapper only clips them visually, it does not
 * stop them existing - which froze the task pane for seconds. Keeping this in
 * one module stops the three copies drifting apart again.
 */

/**
 * Maximum number of matching options rendered into the DOM at once.
 *
 * The footer tells the user how many more matched, and offers "Load all" to
 * lift the cap for the current search. The full filtered list is still
 * computed either way, so the "N more" count stays accurate.
 */
export const MAX_VISIBLE_OPTIONS = 100;

/**
 * Above this many options, the exact-alphabetical re-sort below is replaced
 * by an equivalent but far cheaper two-bucket partition. See below.
 */
export const FAST_PATH_THRESHOLD = 2000;

/**
 * Filter + order the option list for the given search term.
 *
 * Small lists (tags, activity codes, task codes, ...) keep the ORIGINAL
 * filter+sort verbatim, so their behaviour is byte-for-byte unchanged.
 *
 * Large lists (matters, contact emails) take a fast path: `localeCompare` is
 * one of the slowest string operations in JS, and running a full sort with it
 * over 50k-100k entries on every keystroke was a large part of the freeze.
 * The fast path produces the same ordering without any comparator calls -
 * "starts with the search term" first, everything else after - because both
 * buckets preserve the input order, and matterSlice/contactSlice already sort
 * their data alphabetically before it ever reaches here.
 */
export function buildFilteredOptions(options: any[], search: string) {
    const list = options || [];
    const term = (search || "").toLowerCase();

    if (list.length <= FAST_PATH_THRESHOLD) {
        return list
            .filter(option => option.label.toLowerCase().includes(term))
            .sort((a, b) => {
                const aLabel = a.label.toLowerCase();
                const bLabel = b.label.toLowerCase();

                const aStarts = aLabel.startsWith(term);
                const bStarts = bLabel.startsWith(term);

                if (aStarts && !bStarts) return -1;
                if (!aStarts && bStarts) return 1;

                return aLabel.localeCompare(bLabel);
            });
    }

    const startsWith: any[] = [];
    const contains: any[] = [];
    for (let i = 0; i < list.length; i++) {
        const option = list[i];
        const label = (option && typeof option.label === "string") ? option.label.toLowerCase() : "";
        if (!label) continue;
        if (term === "" || label.startsWith(term)) {
            startsWith.push(option);
        } else if (label.includes(term)) {
            contains.push(option);
        }
    }
    return startsWith.concat(contains);
}

/**
 * Filters the options for the current search and caps how many are rendered,
 * plus the opt-in "Load all" escape hatch.
 *
 * `isOpen` is passed in so the cap is restored when the list closes.
 */
export function useCappedOptions(options: any[], search: string, isOpen: boolean) {
    // Set by the footer's "Load all" button - renders every match instead of
    // just the first MAX_VISIBLE_OPTIONS. Scoped to the current search term
    // (see the reset effects below) so it never silently persists into a
    // broader search that would render far more rows than the user agreed to.
    const [showAllOptions, setShowAllOptions] = useState(false);
    // True for the single frame between the click and the big render, so the
    // button can paint "Loading all..." instead of looking like a dead click
    // while React builds the rows.
    const [isExpanding, setIsExpanding] = useState(false);

    // Memoised so it runs once per (options, search) change instead of on
    // every single render of the parent form.
    const filteredOptions = useMemo(
        () => buildFilteredOptions(options, search),
        [options, search]
    );

    const visibleOptions = useMemo(
        () => (showAllOptions ? filteredOptions : filteredOptions.slice(0, MAX_VISIBLE_OPTIONS)),
        [filteredOptions, showAllOptions]
    );

    const hiddenCount = filteredOptions.length - visibleOptions.length;

    // Typing again, or the list itself changing, puts the cap back. Without
    // this, one "Load all" on a narrow search would leak into every later
    // search in the same session.
    useEffect(() => {
        setShowAllOptions(false);
        setIsExpanding(false);
        // Keyed on length, not the array itself: some callers pass an inline
        // options array that is a new reference on every parent render, which
        // would otherwise undo "Load all" on the next unrelated re-render.
    }, [search, options?.length]);

    // Closing the list also resets, so reopening starts capped again.
    useEffect(() => {
        if (!isOpen) {
            setShowAllOptions(false);
            setIsExpanding(false);
        }
    }, [isOpen]);

    // Defer the expensive render by one tick so "Loading all..." actually
    // paints before React starts building thousands of rows.
    const loadAll = useCallback(() => {
        setIsExpanding(true);
        setTimeout(() => {
            setShowAllOptions(true);
            setIsExpanding(false);
        }, 0);
    }, []);

    return {
        filteredOptions,
        visibleOptions,
        hiddenCount,
        showAllOptions,
        isExpanding,
        loadAll,
    };
}

/**
 * Sticky footer under the option list: the "N more" hint with the "Load all"
 * action, or the confirmation once everything is on screen.
 *
 * Renders nothing when the whole (small) list already fits, so short lists
 * such as tags and task codes look exactly as they did before.
 */
export const LoadAllFooter = ({
    filteredCount,
    hiddenCount,
    showAllOptions,
    isExpanding,
    onLoadAll,
}: {
    filteredCount: number;
    hiddenCount: number;
    showAllOptions: boolean;
    isExpanding: boolean;
    onLoadAll: () => void;
}) => {
    if (hiddenCount > 0) {
        const visibleCount = filteredCount - hiddenCount;
        return (
            <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-3 py-2">
                {/* Counts on two tight lines instead of one long sentence: the
                    task pane is narrow, and the old single line wrapped
                    mid-phrase right next to the button. */}
                <div className="min-w-0 leading-tight">
                    <div className="text-[11px] font-medium text-gray-600">
                        Showing {visibleCount.toLocaleString()} of {filteredCount.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-gray-400">Type to narrow down</div>
                </div>
                <button
                    type="button"
                    disabled={isExpanding}
                    // The panel closes on outside mousedown; this button lives
                    // inside the dropdown ref so it is safe, but stopPropagation
                    // keeps the click from bubbling into a parent open/close
                    // handler and closing the list before the rows appear.
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation();
                        onLoadAll();
                    }}
                    className={`shrink-0 whitespace-nowrap rounded-md border px-2.5 py-1 text-[11px] font-semibold shadow-sm transition-colors ${isExpanding
                        ? "cursor-wait border-gray-200 bg-white text-gray-400"
                        : "border-blue-200 bg-white text-blue-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                        }`}
                >
                    {isExpanding ? "Loading..." : "Load all"}
                </button>
            </div>
        );
    }

    if (showAllOptions && filteredCount > MAX_VISIBLE_OPTIONS) {
        return (
            <div className="sticky bottom-0 border-t border-gray-200 bg-gray-50 px-3 py-2 text-[11px] font-medium text-gray-500">
                Showing all {filteredCount.toLocaleString()} records
            </div>
        );
    }

    return null;
};
LoadAllFooter.displayName = "LoadAllFooter";
